import React from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DialogFooter } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, UserPlus, Eye, EyeOff, Save, Target } from 'lucide-react';

/**
 * UserFormContent - Extracted form component to prevent scroll issues
 */
const UserFormContent = ({
  formData,
  setFormData,
  onSubmit,
  submitText,
  formLoading = false,
  isEdit = false,
  showPassword = false,
  setShowPassword,
  onCancel,
  managerOptions = [],
  currentUserId = null,
}) => {
  return (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
      <div className="space-y-2">
        <Label>Full Name *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="John Doe"
          data-testid="user-name-input"
        />
      </div>
      
      <div className="space-y-2">
        <Label>Email *</Label>
        <Input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="john@company.com"
          data-testid="user-email-input"
          disabled={isEdit}
        />
        {isEdit && (
          <p className="text-xs text-slate-500">Email cannot be changed</p>
        )}
      </div>
      
      {!isEdit && (
        <div className="space-y-2">
          <Label>Password *</Label>
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Min 6 characters"
              data-testid="user-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
      
      <div className="space-y-2">
        <Label>Role *</Label>
        <Select
          value={formData.role}
          onValueChange={(value) => {
            // Apply role-based defaults for `also_recruits` whenever the role
            // changes. Mirrors the backend defaults so the UI stays in sync.
            const next = { ...formData, role: value };
            if (value === 'team_lead') next.also_recruits = formData.also_recruits ?? true;
            else if (value === 'delivery_manager') next.also_recruits = formData.also_recruits ?? false;
            else next.also_recruits = undefined; // hidden checkbox for other roles
            setFormData(next);
          }}
        >
          <SelectTrigger data-testid="user-role-select" id="user-form-role-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="delivery_manager">Delivery Manager</SelectItem>
            <SelectItem value="team_lead">Team Lead</SelectItem>
            <SelectItem value="recruiter">Recruiter</SelectItem>
            <SelectItem value="client_spoc">Client SPOC</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Also does recruitment — only meaningful for team_lead / delivery_manager.
          Defaults are applied server-side: TL → checked, DM → unchecked. */}
      {(formData.role === 'team_lead' || formData.role === 'delivery_manager') ? (
        <label
          className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 cursor-pointer hover:bg-slate-100 transition"
          data-testid="user-also-recruits-row"
        >
          <Checkbox
            checked={!!formData.also_recruits}
            onCheckedChange={(v) => setFormData({ ...formData, also_recruits: !!v })}
            data-testid="user-also-recruits-checkbox"
            className="mt-0.5"
          />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
              <Target className="w-3 h-3 text-teal-600" />
              Also does recruitment
            </div>
            <div className="text-[11px] text-slate-500 leading-snug">
              When ticked, this {formData.role === 'team_lead' ? 'team lead' : 'delivery manager'} can be
              assigned jobs and their personal closures count toward team goals on /job-allocation
              and /my-team. Defaults: team-leads ON, delivery-managers OFF.
            </div>
          </div>
        </label>
      ) : null}

      {/* Reports to — optional direct-manager assignment. Only shown for non-admin
          roles since admins don't report to anyone in this app. Options come from
          the parent (pre-filtered to managers in the same org). */}
      {formData.role !== 'admin' && managerOptions && managerOptions.length > 0 ? (
        <div className="space-y-2">
          <Label>Reports to</Label>
          <Select
            value={formData.reports_to || '__none__'}
            onValueChange={(value) =>
              setFormData({ ...formData, reports_to: value === '__none__' ? '' : value })
            }
          >
            <SelectTrigger data-testid="user-reports-to-select">
              <SelectValue placeholder="No manager" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__" data-testid="user-reports-to-none">
                No manager
              </SelectItem>
              {managerOptions
                .filter((m) => m.id !== currentUserId) // a user cannot report to themselves
                .map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}{' '}
                    <span className="text-[10px] text-slate-400 ml-1">({m.role.replace('_', ' ')})</span>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500">
            Used for "My Team" dashboards — lets their manager/team-lead see their performance.
          </p>
        </div>
      ) : null}

      <DialogFooter>
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          onClick={onSubmit}
          disabled={formLoading || !formData.name || !formData.email || (!isEdit && !formData.password)}
          className="bg-teal-700 hover:bg-teal-800"
          data-testid={isEdit ? "update-user-btn" : "create-user-btn"}
        >
          {formLoading ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : isEdit ? (
            <Save className="w-4 h-4 mr-2" />
          ) : (
            <UserPlus className="w-4 h-4 mr-2" />
          )}
          {submitText}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default UserFormContent;
